import { FC, useCallback } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { HeaderContext } from "@tanstack/table-core";
import { useEffectOnceWhen } from "rooks";
import { useDataGridContext } from "../../../../../../../../../../../../storybook/data-grid/DataGridProvider";
import { metricCellFrequencyValues } from "../utils/constants";
import { getMetricFrequencyKey } from "../utils/helpers/helpers";

interface ResourceMetricsHeaderCellProps extends HeaderContext<any, any> {
  defaultFrequency?: keyof typeof metricCellFrequencyValues;
}

export const ResourceMetricsHeaderCell: FC<ResourceMetricsHeaderCellProps> = ({
  column,
  defaultFrequency,
}) => {
  const context = useDataGridContext();
  const data = context?.cellSpecificMetadata?.data;
  const setData = context?.cellSpecificMetadata?.setData;
  const metricCellFrequencyKey = getMetricFrequencyKey(column.id);

  const value = data?.[metricCellFrequencyKey];
  const title = column.columnDef.meta?.headerTitle ?? "";

  const changeHandler = useCallback(
    (e: any) => {
      setData((data) => ({
        ...data,
        [metricCellFrequencyKey]: e.target.value,
      }));
    },
    [setData, metricCellFrequencyKey],
  );

  useEffectOnceWhen(
    () => {
      const val =
        defaultFrequency ?? Object.keys(metricCellFrequencyValues).at(0);

      setData((data) => ({ ...data, [metricCellFrequencyKey]: val }));
    },
    (!value || !!defaultFrequency) && !!setData,
  );

  if (!data || !setData) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="inherit">{title}</Typography>
      <FormControl size="xsmall" focused={false}>
        <Select
          value={value ?? ""}
          sx={{ "& .MuiSelect-select": { pl: 0.5 } }}
          onChange={changeHandler}
        >
          {Object.keys(metricCellFrequencyValues).map((key) => {
            return (
              <MenuItem value={key} key={key}>
                {key}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
};
